import React, { useState, useCallback, useEffect, useMemo, useRef } from 'react'
import { Select, Spin, Tabs, Input, Pagination, message, Tooltip } from 'antd'
import { ArrowLeftOutlined, UserOutlined, FileTextOutlined, CheckCircleOutlined, ExperimentOutlined, SearchOutlined, UserAddOutlined, DownloadOutlined } from '@ant-design/icons'
import { Button as AntButton } from 'antd'
import { useDispatch } from 'react-redux'
import styled from 'styled-components'
import { exportStudentData } from '../../actions'
import instituteRequest from 'utils/instituteRequest'
import studentRequest from 'utils/studentRequest'
import adminRequest from 'utils/adminRequest'
import bulkAddStudentsToAssessment from 'utils/bulkAddStudentsToAssessment'
import InfoCardsUpdate from 'components/InfoCardsUpdate/cardDetails'
import { StyledTable } from 'components/TableStyles/TableStyles'
import StudentReportModal from '../StudentReport'
import { ATTEMPT_STATUS, ATTEMPT_STATUS_TOOLTIP, resolveAttemptStatus } from '../attemptStatus'
import EnhancedBulkUploadDrawer from '../CreateAssessment/Drawers/EnhancedBulkUploadDrawer'

import {
  SectionWrapper,
  TopSection,
  TopHeader,
  CardRowSpaceBetween,
  SearchSection,
  ShowingText,
  TableTop,
  AssessmentName,
} from '../../Style/style'

import UnifiedAssessmentTable from '../ActiveCollegeList/UnifiedAssessmentTable'

const { Option } = Select

const StyledTabs = styled(Tabs)`
  .ant-tabs-nav {
    margin-bottom: 0;
    background: #F5F5F5;
    border-radius: 12px;
    padding: 0;
    box-shadow: none;
    width: 100% !important;
    margin: 0 !important;
  }
  
  .ant-tabs-tab {
     flex: 0 0 calc(100% / ${props => props.$tabCount || 1}) !important;
    max-width: calc(100% / ${props => props.$tabCount || 1}) !important;
    padding: 16px 0 !important;
    font-weight: 400 !important;
    font-size: 14px !important;
    color: #000 !important;
    text-align: center;
    white-space: normal;
    display: flex;
    justify-content: center;
    align-items: center;
    box-sizing: border-box !important;
    transition: none !important;
    border: none !important;
    margin: 0 !important;
    
    &:hover {
      color: #3D5AFE;
      background: transparent;
    }
    
    &:first-child {
      border-top-left-radius: 12px;
      border-bottom-left-radius: 12px;
    }
    
    &:last-child {
      border-top-right-radius: 12px;
      border-bottom-right-radius: 12px;
    }
    
    &.ant-tabs-tab-active {
      background: #3D5AFE !important;
      border-radius: 12px;
      padding: 16px 0 !important;
      
      &:first-child {
        border-top-left-radius: 12px;
        border-bottom-left-radius: 12px;
      }
      
      &:last-child {
        border-top-right-radius: 12px;
        border-bottom-right-radius: 12px;
      }
      
      .ant-tabs-tab-btn {
        color: #FFFFFF !important;
        font-weight: 700 !important;
      }
      
      &:hover {
        background: #3D5AFE !important;
        
        .ant-tabs-tab-btn {
          color: #FFFFFF !important;
        }
      }
    }
  }
  
  .ant-tabs-ink-bar {
    display: none;
  }
  
  .ant-tabs-nav::before {
    border-bottom: none;
  }
  
  .ant-tabs-nav-wrap {
    padding: 0 !important;
    width: 100% !important;
  }
  
  .ant-tabs-nav-list {
    gap: 0;
    width: 100% !important;
    flex: 1;
    margin: 0 !important;  // Remove any default margins
  }
  
  // Ensure no extra spacing on the container
  .ant-tabs-content-holder {
    margin: 0 !important;
    padding: 0 !important;
  }
  
  .ant-tabs-nav-operations {
    display: none !important;
  }
`

const TabWrapper = styled.div`
  margin: 16px 0 24px 0;
  padding: 0 24px;
`

const BackButton = styled.div`
  display: flex;
  align-items: center;
  cursor: pointer;
  margin-right: 12px;
  padding: 4px;

  &:hover {
    color: #3249D7;
  }
`

const CandidateInfo = styled.div`
  display: flex;
  align-items: center;
  cursor: pointer;
`

const CandidateName = styled.div`
  font-weight: 600;
  color: #3249D7;
  font-size: 14px;
  line-height: 1.4;

  &:hover {
    text-decoration: underline;
  }
`

const CandidateId = styled.div`
  font-size: 12px;
  color: #6b7280;
  line-height: 1.4;
`

const typeMap = {
  communication: 'Communication Assessment',
  behavior: 'Behavioral Assessment',
  aptitude: 'Aptitude',
  role_based: 'Role Based Assessment',
  technical_mcq: 'Technical MCQ',
  technical_coding: 'Technical Coding',
}

const normalizeType = (text) => (text || '').toLowerCase().replace(/[\s-]+/g, '_')

const CorporateAssessmentDashboard = ({ corporate, onBack }) => {
  const corporateId = corporate?.id
  const corporateName = corporate?.name || 'Corporate'
  const dispatch = useDispatch()

  // Assessment list state
  const [loading, setLoading] = useState(false)
  const [activeAssessments, setActiveAssessments] = useState([])
  const [completedAssessments, setCompletedAssessments] = useState([])
  const [activePagination, setActivePagination] = useState({ page: 1, limit: 10, totalItems: 0 })
  const [completedPagination, setCompletedPagination] = useState({ page: 1, limit: 10, totalItems: 0 })
  const [filters, setFilters] = useState({})
  // Remember the exact params of the last fetch so post-update refreshes stay on the same page/filter.
  const lastFetchParamsRef = useRef({})
  const [selectedAssessmentFilter, setSelectedAssessmentFilter] = useState('all')
  const [assessmentTypes, setAssessmentTypes] = useState([])
  const [kpiData, setKpiData] = useState({
    totalCandidates: 0,
    totalAssessmentsSent: 0,
    totalAssessmentsTaken: 0,
    totalPracticeTests: 0,
  })

  // Student list state
  const [selectedAssessment, setSelectedAssessment] = useState(null)
  const [students, setStudents] = useState([])
  // Role-based: sections selected at creation (count > 0), from the API — drives
  // the role-based score columns so they show even before students are scored.
  const [roleBasedSections, setRoleBasedSections] = useState([])
  const [studentLoading, setStudentLoading] = useState(false)
  const [studentPagination, setStudentPagination] = useState({ page: 1, limit: 10, totalCount: 0 })
  const [studentSearch, setStudentSearch] = useState('')
  const [studentSortBy, setStudentSortBy] = useState({ field: 'name', order: 'asc' })
  // Candidate status filter (server-side) + export
  const [studentStatusFilter, setStudentStatusFilter] = useState([])
  const [exporting, setExporting] = useState(false)

  // Report drawer state
  const [studentForReport, setStudentForReport] = useState(null)

  // Add candidate state
  const [addCandidateDrawerVisible, setAddCandidateDrawerVisible] = useState(false)
  const [selectedAssessmentForAdd, setSelectedAssessmentForAdd] = useState(null)
  const [addCandidateLoading, setAddCandidateLoading] = useState(false)

  // Fetch KPI states
  const fetchKPIStates = useCallback(async () => {
    if (!corporateId) return
    try {
      const response = await studentRequest.get(`/students/assessments/getAssessmentStatesForCorporate/${corporateId}`)
      const data = response?.data?.data
      if (data) {
        setKpiData({
          totalCandidates: data.totalCandidates || 0,
          totalAssessmentsSent: data.totalAssessmentsSent || 0,
          totalAssessmentsTaken: data.totalAssessmentsTaken || 0,
          totalPracticeTests: data.totalPracticeTests || 0,
        })
      }
    } catch (error) {
      console.error('Error fetching corporate KPI states:', error)
    }
  }, [corporateId])

  // Fetch assessment types dynamically
  const fetchAssessmentTypes = useCallback(async () => {
    if (!corporateId) return
    try {
      const response = await instituteRequest.get('/institutes/studentListInfo/getAssessmentTypes', {
        params: { corporateId },
      })
      const types = response?.data?.data || []
      setAssessmentTypes(types)
    } catch (error) {
      console.error('Error fetching assessment types:', error)
    }
  }, [corporateId])

  // Fetch corporate assessments
  const fetchAssessments = useCallback(async (params = {}) => {
    if (!corporateId) return
    lastFetchParamsRef.current = params
    setLoading(true)
    try {
      const typeNameMap = { 'all': '' }
      assessmentTypes.forEach(t => {
        const key = t.typeName?.toLowerCase() || t.key
        typeNameMap[key] = t.typeName || key
      })

      const requestParams = {
        entityType: 'corporate',
        corporateId,
        order: 'createdAt',
        sort: 'desc',
        pageNo: params.pageNo || 0,
        pageLimit: params.pageLimit || 10,
        type_name: params.type_name !== undefined ? params.type_name : (typeNameMap[selectedAssessmentFilter] || ''),
        ...params,
      }

      const response = await instituteRequest.get('/institutes/studentListInfo/getCorporateAssessmentsInfo', { params: requestParams })
      const data = response?.data?.data

      const allSchedules = data?.data || []
      const active = allSchedules.filter(s => s.status !== 'Completed')
      const completed = allSchedules.filter(s => s.status === 'Completed')

      setActiveAssessments(active)
      setCompletedAssessments(completed)
      setActivePagination({
        page: (data?.pagination?.currentPage || 1),
        limit: data?.pagination?.pageSize || 10,
        totalItems: data?.pagination?.totalCount || 0,
      })
      setCompletedPagination({
        page: 1,
        limit: 10,
        totalItems: completed.length,
      })
    } catch (error) {
      console.error('Error fetching corporate assessments:', error)
      setActiveAssessments([])
      setCompletedAssessments([])
    } finally {
      setLoading(false)
    }
  }, [corporateId, selectedAssessmentFilter, assessmentTypes])

  // Fetch student list for a specific assessment
  const fetchStudentList = useCallback(async (assessment, params = {}) => {
    if (!corporateId || !assessment) return
    const assessmentCorporateMapId = assessment.assessmentCorporateMapId || assessment.id
    if (!assessmentCorporateMapId) return

    setStudentLoading(true)
    try {
      const response = await studentRequest.post(
        `/students/assessments/corporate/student-list/${corporateId}`,
        {
          assessmentCorporateMapId,
          page: params.page || 1,
          limit: params.limit || 10,
          search: params.search || '',
          sortBy: params.sortBy || { field: 'name', order: 'asc' },
          statusFilter: params.statusFilter || [],
        }
      )
      const data = response?.data?.data
      setStudents(data?.students || [])
      setRoleBasedSections(Array.isArray(data?.roleBasedSections) ? data.roleBasedSections : [])
      setStudentPagination({
        page: data?.pageNumber || 1,
        limit: data?.pageSize || 10,
        totalCount: data?.totalCount || 0,
      })
    } catch (error) {
      console.error('Error fetching corporate student list:', error)
      setStudents([])
      setRoleBasedSections([])
      setStudentPagination({ page: 1, limit: 10, totalCount: 0 })
    } finally {
      setStudentLoading(false)
    }
  }, [corporateId])

  useEffect(() => {
    if (corporateId) {
      fetchKPIStates()
      fetchAssessmentTypes()
    }
  }, [corporateId, fetchKPIStates, fetchAssessmentTypes])

  useEffect(() => {
    if (corporateId) fetchAssessments()
  }, [corporateId, selectedAssessmentFilter, fetchAssessments])

  const handleMenuClick = (filterType) => {
    setSelectedAssessmentFilter(filterType)
  }

  // Handle assessment click — show student list internally
  const handleAssessmentClick = (assessment) => {
    setSelectedAssessment(assessment)
    setStudentSearch('')
    setStudentSortBy({ field: 'name', order: 'asc' })
    setStudentStatusFilter([])
    fetchStudentList(assessment, { page: 1, limit: 10, search: '', sortBy: { field: 'name', order: 'asc' }, statusFilter: [] })
  }

  // Handle add candidate
  const handleAddCandidate = useCallback((record) => {
    setSelectedAssessmentForAdd(record)
    setAddCandidateDrawerVisible(true)
  }, [])

  const handleAddCandidateSubmit = useCallback(async (candidates) => {
    if (!candidates || candidates.length === 0) {
      message.warning('No candidates to add')
      return
    }

    setAddCandidateLoading(true)
    const progressKey = 'add-corporate-candidates-progress'
    try {
      const bulkUploadData = candidates.map(c => ({
        first_name: c.firstName || c.first_name || '',
        last_name: c.lastName || c.last_name || '',
        email: c.email || c.primary_email || '',
        primary_email: c.email || c.primary_email || '',
        // Optional mobile for SMS OTP — the drawer captures it as `phone`.
        // Was dropped here, so added candidates never got an OTP SMS. admin-node
        // persists it to assessment_assigned_students.contact_number and the /s/
        // resolver mints it into the invite JWT's phone claim.
        mobile_number: c.phone || c.mobile_number || c.mobile || '',
        // Country code travels as its own field so the backend never has to
        // guess whether mobile_number already contains one.
        country_code: c.countryCode || c.country_code || '',
      }))

      const basePayload = {
        entityType: 'corporate',
        assessmentCorporateMapId: selectedAssessmentForAdd?.assessmentCorporateMapId || selectedAssessmentForAdd?.id,
      }

      const result = await bulkAddStudentsToAssessment({
        client: adminRequest,
        url: '/assessment/addStudentsToAssessment',
        basePayload,
        bulkUploadData,
        chunkSize: 20,
        onProgress: ({ chunkIndex, totalChunks, addedSoFar, done }) => {
          if (done) {
            message.destroy(progressKey)
            return
          }
          message.loading({
            content: `Adding candidates… batch ${Math.min(chunkIndex + 1, totalChunks)} of ${totalChunks} (${addedSoFar} added)`,
            key: progressKey,
            duration: 0,
          })
        },
      })

      const { addedCount, skippedCount, failedCount, chunkErrors } = result
      if (failedCount === 0) {
        message.success(`Added ${addedCount} candidate(s)${skippedCount > 0 ? `, ${skippedCount} duplicate(s) skipped` : ''}`)
      } else {
        const firstErr = chunkErrors[0]?.message || 'unknown error'
        message.warning(`Added ${addedCount}, skipped ${skippedCount}, ${failedCount} failed (${firstErr}). Re-upload the failed rows to retry.`)
      }

      setAddCandidateDrawerVisible(false)
      setSelectedAssessmentForAdd(null)
      fetchAssessments()
      fetchKPIStates()
    } catch (error) {
      message.destroy(progressKey)
      console.error('Error adding candidates:', error)
      message.error(error?.response?.data?.message || error?.message || 'Failed to add candidates')
    } finally {
      setAddCandidateLoading(false)
    }
  }, [selectedAssessmentForAdd, fetchAssessments, fetchKPIStates])

  // Back from student list to assessment list
  const handleBackFromStudentList = () => {
    setSelectedAssessment(null)
    setStudents([])
    setStudentSearch('')
  }

  // Student list search
  const handleStudentSearch = useCallback((e) => {
    const value = e.target.value
    setStudentSearch(value)
    fetchStudentList(selectedAssessment, {
      page: 1,
      limit: studentPagination.limit,
      search: value,
      sortBy: studentSortBy,
      statusFilter: studentStatusFilter,
    })
  }, [selectedAssessment, studentPagination.limit, studentSortBy, studentStatusFilter, fetchStudentList])

  // Candidate status filter (server-side)
  const handleStudentStatusChange = useCallback((values) => {
    const next = Array.isArray(values) ? values : []
    setStudentStatusFilter(next)
    fetchStudentList(selectedAssessment, {
      page: 1,
      limit: studentPagination.limit,
      search: studentSearch,
      sortBy: studentSortBy,
      statusFilter: next,
    })
  }, [selectedAssessment, studentPagination.limit, studentSearch, studentSortBy, fetchStudentList])

  // Export the corporate candidate list to Excel (reuses admin-node export with
  // entityType=corporate; status='sent' exports all candidates with section columns).
  const handleStudentExport = useCallback(async () => {
    if (!selectedAssessment) return
    const assessmentCorporateMapId = selectedAssessment.assessmentCorporateMapId || selectedAssessment.id
    if (!assessmentCorporateMapId) return
    // admin-node export takes a single status bucket. When exactly one status is
    // selected, export that bucket; otherwise export all candidates ('sent').
    const BUCKET = { COMPLETED: 'completed', PENDING: 'pending', INPROGRESS: 'inprogress', DROPOUT: 'droppedoff' }
    const statusBucket = studentStatusFilter.length === 1 ? (BUCKET[studentStatusFilter[0]] || 'sent') : 'sent'
    setExporting(true)
    try {
      await dispatch(exportStudentData(assessmentCorporateMapId, 'corporate', statusBucket, {
        search: studentSearch,
        status: studentStatusFilter,
      }))
    } catch (err) {
      console.error('Corporate export failed:', err)
      message.error('Export failed. Please try again.')
    } finally {
      setExporting(false)
    }
  }, [selectedAssessment, studentSearch, studentStatusFilter, dispatch])

  // Student list pagination
  const handleStudentPageChange = useCallback((page) => {
    fetchStudentList(selectedAssessment, {
      page,
      limit: studentPagination.limit,
      search: studentSearch,
      sortBy: studentSortBy,
      statusFilter: studentStatusFilter,
    })
  }, [selectedAssessment, studentPagination.limit, studentSearch, studentSortBy, studentStatusFilter, fetchStudentList])

  // Student list sort
  const handleStudentTableChange = useCallback((pagination, tableFilters, sorter) => {
    if (sorter && sorter.field) {
      const newSort = {
        field: sorter.field,
        order: sorter.order === 'descend' ? 'desc' : 'asc',
      }
      setStudentSortBy(newSort)
      fetchStudentList(selectedAssessment, {
        page: 1,
        limit: studentPagination.limit,
        search: studentSearch,
        sortBy: newSort,
        statusFilter: studentStatusFilter,
      })
    }
  }, [selectedAssessment, studentPagination.limit, studentSearch, studentStatusFilter, fetchStudentList])

  // Build dynamic columns for student table based on assessment type
  const studentColumns = useMemo(() => {
    if (!selectedAssessment) return []

    const assessmentTypeLower = normalizeType(selectedAssessment.assessmentType)

    const baseColumns = [
      {
        title: 'Candidate',
        dataIndex: 'name',
        key: 'name',
        sorter: true,
        width: 250,
        fixed: 'left',
        render: (text, record) => {
          const hasName = record.name && record.name !== '-'
          const hasEmail = record.email && record.email !== '-'
          if (!hasName && !hasEmail) return '-'
          return (
            <CandidateInfo onClick={() => setStudentForReport(record)}>
              <div>
                {hasName && <CandidateName>{record.name}</CandidateName>}
                {hasEmail && <CandidateId>{record.email}</CandidateId>}
              </div>
            </CandidateInfo>
          )
        },
      },
      {
        title: 'Status',
        dataIndex: 'status',
        key: 'status',
        width: 140,
        align: 'center',
        render: (val, record) => {
          const key = resolveAttemptStatus(record)
          // Unknown backend values are shown as sent rather than guessed.
          const status = ATTEMPT_STATUS[key] || { label: val, color: '#6b7280' }
          const label = <span style={{ color: status.color, fontWeight: 600 }}>{status.label || '-'}</span>
          return ATTEMPT_STATUS_TOOLTIP[key]
            ? <Tooltip title={ATTEMPT_STATUS_TOOLTIP[key]}><span tabIndex={0}>{label}</span></Tooltip>
            : label
        },
      },
      {
        title: 'Total Score',
        dataIndex: 'totalScore',
        key: 'totalScore',
        width: 120,
        align: 'center',
        sorter: true,
        render: (_, r) => {
          const score = r.totalScore
          return score != null ? <span style={{ fontWeight: 600 }}>{score}%</span> : '-'
        },
      },
    ]

    // Add section score columns based on assessment type — matches institute CandidateList pattern
    if (assessmentTypeLower === 'communication') {
      const allSections = ['Reading', 'Listening', 'Speaking', 'Writing'];
      const SUBSECTION_TO_GROUP = {
        'Paragraph Reading': 'Reading', 'Audio Question': 'Listening',
        'Video Response': 'Speaking', 'Question Based Response': 'Writing',
        'Email Writing': 'Writing', 'Dictation': 'Writing',
        'Sentence Completion': 'Writing', 'Sentence Build': 'Writing',
      };
      const es = selectedAssessment?.enabledSections;
      const normalizedEnabled = es && es.length > 0
        ? [...new Set(es.map(s => SUBSECTION_TO_GROUP[s] || s))]
        : allSections;
      allSections.filter(sec => normalizedEnabled.includes(sec)).forEach(sec => {
        baseColumns.push({
          title: sec,
          dataIndex: sec.toLowerCase(),
          key: sec.toLowerCase(),
          width: 110,
          align: 'center',
          sorter: true,
          render: (_, r) => r.sectionScores?.[sec.toLowerCase()] != null ? r.sectionScores[sec.toLowerCase()] : '-',
        })
      })
    } else if (assessmentTypeLower === 'aptitude') {
      ;['Critical', 'Quantitative', 'Logical'].forEach(sec => {
        baseColumns.push({
          title: sec,
          dataIndex: sec.toLowerCase(),
          key: sec.toLowerCase(),
          width: 110,
          align: 'center',
          sorter: true,
          render: (_, r) => r.sectionScores?.[sec.toLowerCase()] != null ? r.sectionScores[sec.toLowerCase()] : '-',
        })
      })
    } else if (assessmentTypeLower === 'role_based') {
      // Role-based columns = the sections SELECTED at creation (count > 0),
      // driven by `roleBasedSections` from the API so they show even before any
      // student is scored. Falls back to the section keys present on the rows.
      const RB_LABELS = {
        'mcq question': 'MCQ', 'subjective question': 'Subjective',
        'video response': 'Video', 'coding question': 'Coding',
      }
      const RB_ORDER = Object.keys(RB_LABELS)
      let defs = Array.isArray(roleBasedSections) ? roleBasedSections.filter(Boolean) : []
      if (defs.length === 0) {
        const seen = new Set()
        students.forEach(s => {
          if (s.sectionScores) Object.keys(s.sectionScores).forEach(k => { if (RB_LABELS[k]) seen.add(k) })
        })
        defs = RB_ORDER.filter(k => seen.has(k)).map(k => ({ key: k, label: RB_LABELS[k] }))
      }
      defs.forEach(def => {
        baseColumns.push({
          title: def.label || RB_LABELS[def.key] || def.key,
          key: def.key,
          width: 110,
          align: 'center',
          render: (_, r) => {
            const v = r.sectionScores?.[def.key]
            return (v != null && typeof v === 'number') ? Math.round(v * 100) / 100 : '-'
          },
        })
      })
    } else if (assessmentTypeLower === 'behavior') {
      // Behavior competencies are fully dynamic — derive from the row data.
      const allKeys = new Set()
      students.forEach(s => {
        if (s.sectionScores) Object.keys(s.sectionScores).forEach(k => allKeys.add(k))
      })
      Array.from(allKeys).slice(0, 6).forEach(key => {
        const capitalize = (str) => str.charAt(0).toUpperCase() + str.slice(1)
        baseColumns.push({
          title: capitalize(key),
          dataIndex: ['sectionScores', key],
          key,
          width: 110,
          align: 'center',
          render: (v) => v != null ? Math.round(v * 100) / 100 : '-',
        })
      })
    } else if (assessmentTypeLower === 'custom_assessment') {
      // Extract section names from sectionWiseStats inside sectionScores
      const allSections = new Set()
      students.forEach(s => {
        const stats = s.sectionScores?.sectionWiseStats
        if (stats && typeof stats === 'object') {
          Object.keys(stats).forEach(k => allSections.add(k))
        }
      })
      Array.from(allSections).slice(0, 6).forEach(key => {
        const sectionData = (() => {
          for (const s of students) {
            const stat = s.sectionScores?.sectionWiseStats?.[key]
            if (stat) return stat
          }
          return null
        })()
        const title = sectionData?.section_name || key.charAt(0).toUpperCase() + key.slice(1)
        baseColumns.push({
          title,
          key,
          width: 120,
          align: 'center',
          render: (_, r) => {
            const stat = r.sectionScores?.sectionWiseStats?.[key]
            return stat?.percentage != null ? `${stat.percentage}%` : '-'
          },
        })
      })
    }

    return baseColumns
  }, [selectedAssessment, students, roleBasedSections])

  const kpiCards = [
    {
      text: 'Total Candidates',
      value: kpiData.totalCandidates,
      padding: '16px',
      borderColor: '#BFBFBF',
      isSelected: false,
      textColor: '#3249D7',
      isFirst: true,
      icon: <UserOutlined style={{ fontSize: 20, color: '#3249D7' }} />,
    },
    {
      text: 'Total Assmts. Sent',
      value: kpiData.totalAssessmentsSent,
      padding: '16px',
      borderColor: '#BFBFBF',
      isSelected: false,
      textColor: '#393939',
      isFirst: false,
      icon: <FileTextOutlined style={{ fontSize: 20, color: '#393939' }} />,
    },
    {
      text: 'Total Assmts. Taken',
      value: kpiData.totalAssessmentsTaken,
      padding: '16px',
      borderColor: '#BFBFBF',
      isSelected: false,
      textColor: '#393939',
      isFirst: false,
      icon: <CheckCircleOutlined style={{ fontSize: 20, color: '#393939' }} />,
    },
    {
      text: 'Total Practice Tests',
      value: kpiData.totalPracticeTests,
      padding: '16px',
      borderColor: '#BFBFBF',
      isSelected: false,
      textColor: '#393939',
      isFirst: false,
      icon: <ExperimentOutlined style={{ fontSize: 20, color: '#393939' }} />,
    },
  ]

  const assessmentMenuItems = [
    {
      key: 'all',
      label: <div style={{ textAlign: 'center', lineHeight: '1.6' }}><div>ALL</div></div>,
    },
    ...assessmentTypes.map(t => ({
      key: t.typeName?.toLowerCase() || t.key,
      label: (
        <div style={{ textAlign: 'center', lineHeight: '1.6' }}>
          <div>{t.typeName || t.label}</div>
        </div>
      ),
    })),
  ]

  // Student list view (when an assessment is clicked)
  if (selectedAssessment) {
    const assessmentTypeLower = normalizeType(selectedAssessment.assessmentType)
    const displayType = typeMap[assessmentTypeLower] || selectedAssessment.assessmentType || '-'
    const assessmentName = selectedAssessment.scheduleName || selectedAssessment.name || '-'

    return (
      <SectionWrapper column>
        {/* Header */}
        <TopSection padding="10px 24px 10px 0px">
          <div style={{ display: 'flex', alignItems: 'center', width: '100%', marginBottom: '16px' }}>
            <BackButton onClick={handleBackFromStudentList}>
              <ArrowLeftOutlined style={{ fontSize: 18 }} />
            </BackButton>
            <div>
              <TopHeader style={{ marginBottom: 0 }}>{assessmentName}</TopHeader>
              <div style={{ fontSize: 13, color: '#888', marginTop: 2 }}>
                {displayType} — {corporateName}
              </div>
            </div>
          </div>
        </TopSection>

        {/* Search, status filter, export and showing count */}
        <TableTop>
          <SearchSection>
            <Input
              placeholder="Search by name or email"
              prefix={<SearchOutlined style={{ color: '#bfbfbf' }} />}
              value={studentSearch}
              onChange={handleStudentSearch}
            />
          </SearchSection>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginLeft: 'auto' }}>
            <Select
              mode="multiple"
              allowClear
              placeholder="Filter by status"
              value={studentStatusFilter}
              onChange={handleStudentStatusChange}
              style={{ minWidth: 220 }}
              maxTagCount="responsive"
            >
              <Option value="COMPLETED">{ATTEMPT_STATUS.completed.label}</Option>
              <Option value="INPROGRESS">{ATTEMPT_STATUS.inProgress.label}</Option>
              <Option value="PENDING">{ATTEMPT_STATUS.pending.label}</Option>
              <Option value="DROPOUT">{ATTEMPT_STATUS.dropped.label}</Option>
            </Select>
            <AntButton
              icon={<DownloadOutlined />}
              loading={exporting}
              onClick={handleStudentExport}
              disabled={!selectedAssessment}
            >
              Export
            </AntButton>
            <ShowingText style={{ whiteSpace: 'nowrap' }}>
              Showing {students.length} of {studentPagination.totalCount}
            </ShowingText>
          </div>
        </TableTop>

        {/* Student Table */}
        <Spin spinning={studentLoading}>
          <StyledTable
            columns={studentColumns}
            dataSource={students}
            pagination={false}
            rowKey={record => record.email || record.id}
            scroll={{ x: 900 }}
            onChange={handleStudentTableChange}
          />
        </Spin>

        {/* Pagination */}
        {studentPagination.totalCount > studentPagination.limit && (
          <div style={{ margin: '16px', textAlign: 'center', display: 'flex', justifyContent: 'center' }}>
            <Pagination
              current={studentPagination.page}
              total={studentPagination.totalCount}
              pageSize={studentPagination.limit}
              onChange={handleStudentPageChange}
            />
          </div>
        )}

        {/* Student Report Drawer */}
        <StudentReportModal
          visible={!!studentForReport}
          onClose={() => setStudentForReport(null)}
          student={studentForReport}
          allowProctoring={false}
          enabledSections={selectedAssessment?.enabledSections}
        />
      </SectionWrapper>
    )
  }

  // Assessment list view (default)
  return (
    <SectionWrapper column>
      <>
        {/* Header with Back button and Title */}
        <TopSection padding="10px 24px 10px 0px">
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              width: '100%',
              marginBottom: '24px',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <BackButton onClick={onBack}>
                <ArrowLeftOutlined style={{ fontSize: 18 }} />
              </BackButton>
              <TopHeader>{corporateName}</TopHeader>
            </div>
          </div>
        </TopSection>

        {/* KPI Cards */}
        <CardRowSpaceBetween spaceBetween>
          {kpiCards.map((kpi, index) => (
            <InfoCardsUpdate
              key={index}
              padding={kpi.padding}
              width={'100%'}
              height={'100px'}
              text=""
              IconText={kpi.text}
              value={kpi.value}
              borderColor={`1px solid ${kpi.borderColor}`}
              isClickable={false}
              isSelected={kpi.isSelected}
              backgroundColor={'#ffffff'}
              textColor={kpi.textColor}
              icon={kpi.icon}
              isFirst={kpi.isFirst}
              style={{ cursor: 'default' }}
            />
          ))}
        </CardRowSpaceBetween>

        {/* Pill-style Assessment Type Tabs */}
        <TabWrapper>
          <StyledTabs
            $tabCount={assessmentMenuItems.length}
            activeKey={selectedAssessmentFilter}
            onChange={handleMenuClick}
            items={assessmentMenuItems}
            size="large"
            type="card"
          />
        </TabWrapper>

        {/* Unified Assessment Table — no expandable rows for corporate */}
        <UnifiedAssessmentTable
          activeAssessments={activeAssessments}
          completedAssessments={completedAssessments}
          loading={loading}
          onAssessmentClick={handleAssessmentClick}
          onAddCandidate={handleAddCandidate}
          filters={filters}
          activePage={activePagination.page}
          completedPage={completedPagination.page}
          activeLimit={activePagination.limit}
          completedLimit={completedPagination.limit}
          activeTotalItems={activePagination.totalItems}
          completedTotalItems={completedPagination.totalItems}
          onScheduleUpdated={() => fetchAssessments(lastFetchParamsRef.current)}
          onPageChange={(page) => {
            fetchAssessments({
              pageNo: page - 1,
              pageLimit: activePagination.limit,
            })
          }}
          onFilterChange={(newFilters) => {
            setFilters(newFilters)
            fetchAssessments({
              ...newFilters,
              pageNo: 0,
              pageLimit: activePagination.limit,
            })
          }}
        />

        {/* Add Candidate Drawer */}
        {addCandidateDrawerVisible && (
          <EnhancedBulkUploadDrawer
            visible={addCandidateDrawerVisible}
            onClose={() => {
              setAddCandidateDrawerVisible(false)
              setSelectedAssessmentForAdd(null)
            }}
            assessmentData={{
              entityId: corporateId,
              // Drives the "closes in N days" banner and its Extend action.
              assessmentEndDate: selectedAssessmentForAdd?.endDate,
              assessmentStartDate: selectedAssessmentForAdd?.startDate,
              assessmentCorporateMapId:
                selectedAssessmentForAdd?.assessmentCorporateMapId ||
                selectedAssessmentForAdd?.id,
              // Extending changes the row behind the drawer, so pull the list
              // back in step rather than leaving a stale date on screen.
              onEndDateUpdated: () => {
                fetchAssessments()
                fetchKPIStates()
              },
              preservedCandidates: [],
              preservedUploadSummaries: [],
              timestamp: Date.now(),
            }}
            entityType="corporate"
            mode="addCandidate"
            onDataSubmit={(candidates) => {
              handleAddCandidateSubmit(candidates)
            }}
          />
        )}
      </>
    </SectionWrapper>
  )
}

export default CorporateAssessmentDashboard
